/**
 * Billing (/billing) — T7.
 *
 * Plan cards sourced from the un-gated GET /api/billing/plans (every
 * tenant, free included, can read it — NOT the admin-gated
 * /api/admin/plans). A monthly/annual toggle shows both price points per
 * plan; "Subscribe" starts a Stripe Checkout session
 * (POST /api/billing/stripe/checkout {plan_id, interval} -> redirect to
 * the returned checkout_url) and "Manage billing" opens the Stripe
 * customer portal (POST /api/billing/stripe/portal -> portal_url).
 *
 * Both Stripe endpoints are being built by a parallel lane (T4/T3) — this
 * page codes against the documented contract (services/billingApi.ts) and
 * handles ITS 503 "not configured" response with a clear banner rather
 * than a raw error.
 *
 * A `?plan=<id>` query param (set by Paywall.tsx's upgrade buttons)
 * preselects that plan card — a visual hint only, every card's own
 * Subscribe button still drives checkout independently.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  ButtonGroup,
  Card,
  EmptyState,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from '@shopify/polaris';

import {
  BillingApiError,
  createStripeCheckout,
  createStripePortalSession,
  getBillingPlans,
  type BillingInterval,
  type BillingPlan,
} from '../services/billingApi';
import { fetchEntitlements, type EntitlementsResponse } from '../services/entitlementsApi';
import { recordFunnelEvent } from '../services/funnelApi';
import { EMPTY_STATE_IMAGE } from '../config/emptyState';

const NOT_CONFIGURED_MESSAGE =
  'Billing is not available right now. Please try again shortly, or contact support if this persists.';

// Plan cards stop growing new grid columns past this width — a wide plan
// catalog should wrap to a new row, not squeeze every card down to fit.
const MAX_GRID_COLUMNS = 4;

function formatPrice(cents: number | null): string {
  if (!cents) return 'Free';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function Billing() {
  const [searchParams] = useSearchParams();
  const preselectedPlanId = searchParams.get('plan');

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [entitlements, setEntitlements] = useState<EntitlementsResponse | null>(null);
  const [entitlementsFailed, setEntitlementsFailed] = useState(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionBanner, setActionBanner] = useState<string | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [pendingPortal, setPendingPortal] = useState(false);

  useEffect(() => {
    recordFunnelEvent('pricing_viewed');

    let cancelled = false;
    Promise.allSettled([getBillingPlans(), fetchEntitlements()]).then(
      ([plansResult, entitlementsResult]) => {
        if (cancelled) return;
        if (plansResult.status === 'fulfilled') {
          setPlans(plansResult.value.plans.filter((p) => p.is_active));
        } else {
          setLoadError('Could not load plans. Refresh to try again.');
        }
        if (entitlementsResult.status === 'fulfilled') {
          setEntitlements(entitlementsResult.value);
        } else {
          console.error(
            'Failed to load billing entitlements:',
            entitlementsResult.reason
          );
          setEntitlementsFailed(true);
        }
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCheckout = async (planId: string) => {
    setActionBanner(null);
    setPendingPlanId(planId);
    recordFunnelEvent('checkout_started');
    try {
      const { checkout_url } = await createStripeCheckout(planId, billingInterval);
      window.location.href = checkout_url;
    } catch (err) {
      if (err instanceof BillingApiError && err.status === 503) {
        setActionBanner(NOT_CONFIGURED_MESSAGE);
      } else {
        setActionBanner(
          err instanceof Error ? err.message : 'Could not start checkout.'
        );
      }
      setPendingPlanId(null);
    }
  };

  const handleManageBilling = async () => {
    setActionBanner(null);
    setPendingPortal(true);
    try {
      const { portal_url } = await createStripePortalSession();
      window.location.href = portal_url;
    } catch (err) {
      if (err instanceof BillingApiError && err.status === 503) {
        setActionBanner(NOT_CONFIGURED_MESSAGE);
      } else {
        setActionBanner(
          err instanceof Error ? err.message : 'Could not open the billing portal.'
        );
      }
      setPendingPortal(false);
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Billing">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  return (
    <Page
      title="Billing"
      subtitle="Choose a plan or manage your existing subscription."
      primaryAction={{
        content: 'Manage billing',
        onAction: () => void handleManageBilling(),
        loading: pendingPortal,
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {loadError && (
              <Banner tone="critical" onDismiss={() => setLoadError(null)}>
                {loadError}
              </Banner>
            )}
            {entitlementsFailed && (
              <Banner tone="warning" onDismiss={() => setEntitlementsFailed(false)}>
                We couldn&apos;t confirm your current plan, so it isn&apos;t
                highlighted below — the buttons still work.
              </Banner>
            )}
            {actionBanner && (
              <Banner tone="critical" onDismiss={() => setActionBanner(null)}>
                {actionBanner}
              </Banner>
            )}

            {/* A load failure gets the banner above ONLY — the empty state
                is reserved for a genuine zero-active-plans response, not
                doubling up on the same "no plans" condition the banner
                already covers. */}
            {!loadError && plans.length === 0 ? (
              <Card>
                <EmptyState heading="No plans available" image={EMPTY_STATE_IMAGE}>
                  <p>Check back shortly, or contact support if this persists.</p>
                </EmptyState>
              </Card>
            ) : !loadError ? (
              <>
                <InlineStack align="center">
                  <ButtonGroup variant="segmented">
                    <Button
                      pressed={billingInterval === 'monthly'}
                      onClick={() => setBillingInterval('monthly')}
                    >
                      Monthly
                    </Button>
                    <Button
                      pressed={billingInterval === 'yearly'}
                      onClick={() => setBillingInterval('yearly')}
                    >
                      Annual
                    </Button>
                  </ButtonGroup>
                </InlineStack>

                <InlineGrid
                  columns={{
                    xs: 1,
                    sm: 2,
                    lg: Math.min(plans.length || 1, MAX_GRID_COLUMNS),
                  }}
                  gap="400"
                >
                  {plans.map((plan) => {
                    const primaryPriceCents =
                      billingInterval === 'monthly'
                        ? plan.price_monthly_cents
                        : plan.price_yearly_cents;
                    const isCurrentPlan = plan.id === entitlements?.plan_id;
                    const isPreselected =
                      !isCurrentPlan && plan.id === preselectedPlanId;

                    return (
                      <Card key={plan.id}>
                        <BlockStack gap="400">
                          <BlockStack gap="200">
                            <InlineStack align="space-between" blockAlign="center">
                              <Text as="h3" variant="headingMd">
                                {plan.display_name}
                              </Text>
                              {isCurrentPlan && (
                                <Badge tone="success">Current plan</Badge>
                              )}
                              {isPreselected && <Badge tone="info">Selected</Badge>}
                            </InlineStack>
                            <Text as="p" variant="heading2xl">
                              {formatPrice(primaryPriceCents)}
                              {primaryPriceCents ? (
                                <Text as="span" variant="bodyMd" tone="subdued">
                                  {' '}
                                  /{billingInterval === 'monthly' ? 'month' : 'year'}
                                </Text>
                              ) : null}
                            </Text>
                            {/* Both price points stay visible regardless of
                                the toggle, so switching intervals never
                                hides what the OTHER cadence costs. */}
                            <Text as="p" tone="subdued">
                              {plan.price_monthly_cents
                                ? `${formatPrice(plan.price_monthly_cents)}/month`
                                : 'Free'}
                              {' · '}
                              {plan.price_yearly_cents
                                ? `${formatPrice(plan.price_yearly_cents)}/year`
                                : 'Free'}
                            </Text>
                            {plan.description && (
                              <Text as="p" tone="subdued">
                                {plan.description}
                              </Text>
                            )}
                          </BlockStack>
                          <Button
                            variant="primary"
                            fullWidth
                            disabled={isCurrentPlan}
                            loading={pendingPlanId === plan.id}
                            onClick={() => void handleCheckout(plan.id)}
                          >
                            {isCurrentPlan ? 'Current plan' : 'Subscribe'}
                          </Button>
                        </BlockStack>
                      </Card>
                    );
                  })}
                </InlineGrid>
              </>
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
